import { useAtom } from "jotai";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { UserInfo } from "interfaces/user";
import Avatar from "components/base/Avatar";
import Button from "components/base/Button";
import avatar from "assets/img/team/user.png";
import { extractNameFromURL } from "helpers/utils";
import { user_info } from "jotai/UserJotaiInstance";
import { Card, Col, Row, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUser, 
  faTrophy,
  faLightbulb,
  faFileLines, 
  faGraduationCap
} from "@fortawesome/free-solid-svg-icons";

const CustomerProfileCard = () => {
  const [userInfo] = useAtom<UserInfo>(user_info);

  let attachments = [];
  try {
    attachments = JSON.parse(userInfo.user_attachments || "[]");
  } catch (error) {
    console.error("Failed to parse user attachments:", error);
  }

  return (
    <>
      {/* AI-Enhanced Profile Card */}
      <Card 
        className="border-0 mb-4 ai-hover-lift"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2) !important',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0, 212, 255, 0.1)',
          overflow: 'hidden'
        }}
      >
        {/* AI Intelligence Header */}
        <Card.Header 
          className="border-bottom-0 py-4"
          style={{ 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px 20px 0 0'
          }}
        >
          <div className="d-flex align-items-center justify-content-between card-header-main-flex">
            <div className="d-flex align-items-center card-header-icon-text-flex">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3 icon-wrapper-responsive"
                style={{ 
                  width: "40px", 
                  height: "40px",
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                  boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faUser} 
                  className="text-white" 
                  style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
                />
              </div>
              <div className="card-header-text-content">
                <h5 style={{ 
                  color: '#1f2937',
                  textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  marginBottom: '2px'
                }}>
                  Agent Profile Intelligence
                </h5>
                <small className="text-muted" style={{ fontSize: '12px' }}>
                  AI-powered insights & metrics
                </small>
              </div>
            </div>
            
            {/* AI Status Badge */}
            <Badge 
              bg="transparent" 
              className="fw-semibold card-header-badge-responsive"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                color: '#10b981',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                fontSize: '10px',
                padding: '6px 12px',
                borderRadius: '15px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              <div className="rounded-circle me-1 d-inline-block" style={{
                width: '6px', height: '6px', background: '#10b981',
                animation: 'pulse 2s infinite'
              }} />
              Active
            </Badge>
          </div>
        </Card.Header>

        <Card.Body className="p-4" style={{ background: 'transparent' }}>
          <Row className="align-items-center g-4 text-center text-sm-start">
            <Col xs={12}>
              <div className="position-relative d-inline-block">
                <div 
                  style={{
                    border: '4px solid rgba(0, 212, 255, 0.3)',
                    boxShadow: '0 0 25px rgba(0, 212, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'inline-block'
                  }}
                >
                  <Avatar 
                    size="5xl" 
                    src={userInfo?.user_image || avatar}
                  />
                </div>
                {/* AI Status Indicator on Avatar */}
                <div 
                  className="position-absolute rounded-circle"
                  style={{
                    width: '20px',
                    height: '20px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: '3px solid white',
                    bottom: '10px',
                    right: '10px',
                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.6)',
                    animation: 'pulse 2s infinite'
                  }}
                />
              </div>
            </Col>
            
            <Col xs={12} className="flex-1">
              <h3 style={{ 
                color: '#1f2937',
                textShadow: '0 0 10px rgba(0, 212, 255, 0.2)',
                fontWeight: 600,
                marginBottom: '8px'
              }}>
                {userInfo.user_first_name} {userInfo.user_last_name}
              </h3>
              <p className="text-body-secondary mb-3" style={{ fontSize: '14px' }}>
                {userInfo.user_email}
              </p>

              {userInfo?.user_documents?.resume ? (
                <Button
                  target="_blank"
                  as={Link}
                  to={userInfo?.user_documents?.resume}
                  variant="outline-primary"
                  size="sm"
                  className="py-2 px-3 ai-hover-lift"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    color: '#0ea5e9',
                    fontWeight: 500,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <FontAwesomeIcon icon={faFileLines} className="me-2" />
                  View Resume
                </Button>
              ) : (
                <div 
                  className="text-muted py-2 px-3 rounded-2"
                  style={{ 
                    background: 'rgba(156, 163, 175, 0.1)',
                    border: '1px solid rgba(156, 163, 175, 0.2)',
                    fontSize: '14px'
                  }}
                >
                  <FontAwesomeIcon icon={faFileLines} className="me-2" />
                  Resume Not Available
                </div>
              )}
            </Col>
          </Row>

          {/* About Me Section with AI Enhancement */}
          <div 
            className="my-4 p-3 rounded-3"
            style={{
              background: 'rgba(0, 212, 255, 0.05)',
              border: '1px solid rgba(0, 212, 255, 0.1)'
            }}
          >
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon 
                icon={faLightbulb} 
                className="me-2 text-primary" 
                style={{ color: '#00d4ff' }}
              />
              <p className="fw-semibold mb-0" style={{ color: '#1f2937' }}>About me</p>
            </div>
            <p className="text-body-tertiary mb-0" style={{ fontSize: '14px', lineHeight: '1.6' }}>
              {userInfo.user_description || "No description available"}
            </p>
          </div>

          {/* AI Metrics Section */}
          <div className="border-top border-translucent pt-4">
            <Row className="g-3">
              <Col xs={6}>
                <div 
                  className="text-center p-3 rounded-3 ai-hover-lift"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.2)'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faGraduationCap} 
                    className="text-success mb-2" 
                    size="lg"
                    style={{ color: '#10b981' }}
                  />
                  <h6 className="fw-bold mb-1" style={{ color: '#1f2937' }}>Experience</h6>
                  <div className="fw-bold text-success" style={{ fontSize: '18px' }}>
                    {userInfo.years_of_experience || 0}
                  </div>
                  <small className="text-muted">Years</small>
                </div>
              </Col>
              
              <Col xs={6}>
                <div 
                  className="text-center p-3 rounded-3 ai-hover-lift"
                  style={{
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                    border: '1px solid rgba(245, 158, 11, 0.2)'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={faTrophy} 
                    className="mb-2" 
                    size="lg"
                    style={{ color: '#f59e0b' }}
                  />
                  <h6 className="fw-bold mb-1" style={{ color: '#1f2937' }}>Closed Deals</h6>
                  <div className="fw-bold" style={{ fontSize: '18px', color: '#f59e0b' }}>
                    {userInfo.closed_deal || 0}
                  </div>
                  <small className="text-muted">Total</small>
                </div>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>

      {/* AI-Enhanced Attachments Card */}
      <Card 
        className="border-0 ai-hover-lift"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2) !important',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0, 212, 255, 0.1)'
        }}
      >
        {/* Attachments Header */}
        <Card.Header 
          className="border-bottom-0 py-4"
          style={{ 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px 20px 0 0'
          }}
        >
          <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: "40px", 
                height: "40px",
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
              }}
            >
              <FontAwesomeIcon 
                icon={faFileLines} 
                className="text-white" 
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
              />
            </div>
            <div>
              <h6 className="mb-1" style={{ 
                color: '#1f2937',
                textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                fontWeight: 600
              }}>
                Document Intelligence
              </h6>
              <small className="text-muted">AI-analyzed attachments</small>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4" style={{ background: 'transparent' }}>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {attachments?.length > 0 ? (
              attachments?.map((attachment: string, index: number) => (
                <div
                  key={index}
                  className={classNames(
                    "p-3 rounded-3 mb-3 ai-hover-lift",
                    {
                      "mb-0": index === attachments.length - 1,
                    }
                  )}
                  style={{
                    background: 'rgba(0, 212, 255, 0.05)',
                    border: '1px solid rgba(0, 212, 255, 0.1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center flex-1">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                          boxShadow: '0 0 10px rgba(0, 212, 255, 0.3)'
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faFileLines}
                          className="text-white"
                          size="sm"
                        />
                      </div>
                      <div className="flex-1">
                        <a
                          target="_blank" rel="noreferrer"
                          href={attachment}
                          className="fw-semibold mb-1 d-block"
                          style={{ 
                            wordBreak: "break-all",
                            color: '#1f2937',
                            textDecoration: 'none',
                            fontSize: '14px'
                          }}
                        >
                          {extractNameFromURL(attachment)}
                        </a>
                        <small className="text-muted">AI-processed document</small>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div 
                className="text-center py-5"
                style={{
                  background: 'rgba(156, 163, 175, 0.05)',
                  borderRadius: '16px',
                  border: '2px dashed rgba(156, 163, 175, 0.2)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faFileLines} 
                  size="3x" 
                  className="text-muted mb-3"
                  style={{ opacity: 0.3 }}
                />
                <h6 className="text-muted mb-2">No Attachments Found</h6>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Upload documents to unlock AI insights
                </p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* AI Animation Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .ai-hover-lift {
            transition: all 0.3s ease;
          }
          
          .ai-hover-lift:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 35px rgba(0, 212, 255, 0.15) !important;
          }
          
          @keyframes pulse {
            0%, 100% { 
              opacity: 1; 
              transform: scale(1); 
            }
            50% { 
              opacity: 0.7; 
              transform: scale(1.1); 
            }
          }

          @media (max-width: 500px) {
            .card-header-main-flex {
              flex-direction: column;
              align-items: center; /* Center the stacked items */
            }
            .card-header-icon-text-flex {
              flex-direction: column;
              align-items: center; /* Center the icon and the text block below it */
            }
            .card-header-icon-text-flex > .rounded-circle { /* Target the icon's direct container */
              margin-bottom: 0.5rem; /* Space between icon and text when stacked */
            }
            .card-header-text-content {
              text-align: center;
              width: 100%; /* Ensure it takes full width to center text properly */
            }
            .card-header-badge-responsive {
              margin-top: 0.5rem;
            }
            .icon-wrapper-responsive {
              margin-right: 0 !important;
            }
          }
        `
      }} />
    </>
  );
};

export default CustomerProfileCard;
